import type { Point } from "./cardGeometry";
import { channelsDepth, COLUMN_LAYOUT } from "./columnLayout";
import type { GraphOrientation, NodeBox } from "./graphLayout";
import { nodeAtLevel, SKIP_CHANNELS, skipPlan, type SkipPlan } from "./skipChannels";
import type { GraphEdge, GraphNode, WorkflowGraph } from "./workflowGraph";

/**
 * The path of every edge, in graph coordinates (P-14), from the anchors the cards draw
 * their ports at (`cardGeometry`). Edges never take their ends from measured handles, so
 * they meet their ports whatever the zoom, the page's own scaling, or when the canvas was
 * measured (React Flow measures handles once, divided by its own zoom only). Pure.
 *
 * A pipeline edge to the next step turns once in the middle of the gap between the cards;
 * an edge that skips steps runs in the lane and the channels `skipChannels` gives it,
 * which the layout made room for; an input's edge curves down into its tab; `then` is a
 * straight line between the cards' sides at header height. In the vertical layout (F59) an
 * edge between pipeline steps leaves its source to the right, runs down a channel beside the
 * column, crosses the gap above its target and enters it from the left; an input's line runs
 * down a channel between the column and the inputs and drops into its tab from the gap above
 * the step it feeds, so it passes no card on the way (`columnRoute`).
 */

export interface EdgeRoute {
  readonly source: Point;
  readonly target: Point;
  /** An SVG path from `source` to `target`. */
  readonly path: string;
  /** A box the path stays inside, in graph coordinates: the canvas fits it too. */
  readonly extent: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  /** The corners of its polyline; for a curve, its start, two control points and its end. */
  readonly points: readonly Point[];
  readonly curve: boolean;
}

/** A node's anchors relative to its box: its ports' and its `then` height. */
export interface NodeAnchors {
  readonly anchors: ReadonlyMap<string, Point>;
  readonly thenY: number;
}

export const EDGE_ROUTING = {
  cornerRadius: 8,
} as const;

export function edgeRoutes(graph: WorkflowGraph, boxes: ReadonlyMap<string, NodeBox>, anchorsOf: (node: GraphNode) => NodeAnchors, orientation: GraphOrientation = "horizontal"): ReadonlyMap<string, EdgeRoute> {
  const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
  const origin = absoluteOrigins(graph, boxes);
  const anchors = new Map(graph.nodes.map((node) => [node.id, anchorsOf(node)]));
  const plan = skipPlan(graph);
  const lanes = orientation === "horizontal" ? skipLanes(graph, boxes, origin, plan) : new Map<string, SkipLane>();
  const columns = orientation === "vertical" ? columnChannels(graph, boxes, origin, plan) : null;
  const routes = new Map<string, EdgeRoute>();
  for (const edge of graph.edges) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    const sourceBox = boxes.get(edge.source);
    const targetBox = boxes.get(edge.target);
    const sourceOrigin = origin.get(edge.source);
    const targetOrigin = origin.get(edge.target);
    if (source === undefined || target === undefined || sourceBox === undefined || targetBox === undefined || sourceOrigin === undefined || targetOrigin === undefined) {
      continue;
    }
    const sourceAnchors = anchors.get(source.id);
    const targetAnchors = anchors.get(target.id);
    const start = edge.kind === "then" ? { x: sourceBox.width, y: sourceAnchors?.thenY ?? 0 } : edge.sourcePort === null ? undefined : sourceAnchors?.anchors.get(edge.sourcePort);
    const end = edge.kind === "then" ? { x: 0, y: targetAnchors?.thenY ?? 0 } : edge.targetPort === null ? undefined : targetAnchors?.anchors.get(edge.targetPort);
    if (start === undefined || end === undefined) {
      continue;
    }
    const from = { x: sourceOrigin.x + start.x, y: sourceOrigin.y + start.y };
    const to = { x: targetOrigin.x + end.x, y: targetOrigin.y + end.y };
    const leavesRight = start.x >= sourceBox.width;
    const column = columns?.get(edge.id);
    const points = column !== undefined ? { points: columnRoute(from, to, column), curve: false } : pointsOf(edge, from, to, leavesRight, lanes.get(edge.id) ?? null);
    routes.set(edge.id, { source: from, target: to, path: points.curve ? curvePath(points.points) : roundedPath(points.points), extent: extentOf(points.points), ...points });
  }
  return routes;
}

/**
 * The points of an edge's route: the corners of a polyline, or for an input's curve its
 * start, two control points and its end. The path stays inside their box.
 */
function pointsOf(edge: GraphEdge, from: Point, to: Point, leavesRight: boolean, lane: SkipLane | null): { readonly points: readonly Point[]; readonly curve: boolean } {
  if (edge.route === "feed") {
    // Down from an input's bottom (or out of a step's right side inside an open input) into a tab.
    const drop = Math.max(24, (to.y - from.y) / 2);
    const first = leavesRight ? { x: from.x + drop, y: from.y } : { x: from.x, y: from.y + drop };
    return { points: [from, first, { x: to.x, y: to.y - drop }, to], curve: true };
  }
  if (edge.route === "skip" && lane !== null) {
    return { points: [from, { x: lane.out, y: from.y }, { x: lane.out, y: lane.y }, { x: lane.back, y: lane.y }, { x: lane.back, y: to.y }, to], curve: false };
  }
  return { points: oneTurn(from, to), curve: false };
}

/** A plain route between two points, for an edge whose card is being dragged: one turn in the middle. */
export function pathBetween(from: Point, to: Point): string {
  return roundedPath(oneTurn(from, to));
}

function oneTurn(from: Point, to: Point): Point[] {
  if (Math.abs(from.y - to.y) < 0.5) {
    return [from, to];
  }
  const middle = from.x + (to.x - from.x) / 2;
  return [from, { x: middle, y: from.y }, { x: middle, y: to.y }, to];
}

/** A cubic curve through a start, two control points and an end. */
function curvePath([from, first, second, to]: readonly Point[]): string {
  const at = (point: Point | undefined) => (point === undefined ? "0 0" : `${n(point.x)} ${n(point.y)}`);
  return `M ${at(from)} C ${at(first)} ${at(second)} ${at(to)}`;
}

function extentOf(points: readonly Point[]): EdgeRoute["extent"] {
  const x = Math.min(...points.map((point) => point.x));
  const y = Math.min(...points.map((point) => point.y));
  return { x, y, width: Math.max(...points.map((point) => point.x)) - x, height: Math.max(...points.map((point) => point.y)) - y };
}

/** A polyline of horizontal and vertical runs, its corners rounded. */
export function roundedPath(points: readonly Point[]): string {
  const [first, ...rest] = points;
  if (first === undefined) {
    return "";
  }
  let path = `M ${n(first.x)} ${n(first.y)}`;
  rest.forEach((corner, index) => {
    const before = points[index] ?? first;
    const after = rest[index + 1];
    if (after === undefined) {
      path += ` L ${n(corner.x)} ${n(corner.y)}`;
      return;
    }
    const radius = Math.min(EDGE_ROUTING.cornerRadius, distance(before, corner) / 2, distance(corner, after) / 2);
    const entry = towards(corner, before, radius);
    const exit = towards(corner, after, radius);
    path += ` L ${n(entry.x)} ${n(entry.y)} Q ${n(corner.x)} ${n(corner.y)} ${n(exit.x)} ${n(exit.y)}`;
  });
  return path;
}

interface SkipLane {
  /** Under the row. */
  readonly y: number;
  /** The channel right of the source's card, and left of the target's card. */
  readonly out: number;
  readonly back: number;
}

/** Each skip edge's lane and channels (`skipChannels`), placed along the laid-out row. */
function skipLanes(graph: WorkflowGraph, boxes: ReadonlyMap<string, NodeBox>, origin: ReadonlyMap<string, Point>, plan: SkipPlan): ReadonlyMap<string, SkipLane> {
  const rowBottom = new Map<string | null, number>();
  for (const node of graph.nodes) {
    const box = boxes.get(node.id);
    const at = origin.get(node.id);
    if (node.band === "main" && box !== undefined && at !== undefined) {
      rowBottom.set(node.parentId, Math.max(rowBottom.get(node.parentId) ?? Number.NEGATIVE_INFINITY, at.y + box.height));
    }
  }
  const parentOf = new Map(graph.nodes.map((node) => [node.id, node.parentId]));
  // The card at the edge's level: an end drawn inside an open frame runs beside the frame.
  const cardAt = (id: string, level: string | null) => nodeAtLevel(parentOf, id, level);
  const lanes = new Map<string, SkipLane>();
  for (const edge of graph.edges) {
    const index = plan.lane.get(edge.id);
    if (index === undefined) {
      continue;
    }
    const level = parentOf.get(edge.target) ?? null;
    const source = cardAt(edge.source, level);
    const target = cardAt(edge.target, level);
    const sourceRight = (origin.get(source)?.x ?? 0) + (boxes.get(source)?.width ?? 0);
    const targetLeft = origin.get(target)?.x ?? 0;
    const { runMargin, runSpacing, laneGap, laneSpacing } = SKIP_CHANNELS;
    lanes.set(edge.id, {
      y: (rowBottom.get(level) ?? 0) + laneGap + index * laneSpacing,
      out: sourceRight + runMargin + (plan.leaving.get(edge.id) ?? 0) * runSpacing,
      back: targetLeft - runMargin - (plan.entering.get(edge.id) ?? 0) * runSpacing,
    });
  }
  return lanes;
}

/** Where an edge runs beside a column (F59): between pipeline steps, or from an input into a step. */
type ColumnChannel =
  | {
      readonly kind: "pipeline";
      /** The channel right of the column, and the one left of it. */
      readonly right: number;
      readonly left: number;
      /** Leaving a step inside an open sub-workflow (F61): the feed channel of that step's own column, down which it leaves the group; else `null`. */
      readonly beside: number | null;
      /** Where it crosses the gap above its target. */
      readonly crossing: number;
    }
  | {
      readonly kind: "feed";
      /** Just below the input it leaves, or the open input it leaves from inside. */
      readonly below: number;
      /** Leaving a step inside an open input: the feed channel of that step's own column, down which it leaves the input; else `null`. */
      readonly beside: number | null;
      /** The channel between the column's channels and the inputs. */
      readonly channel: number;
      /** Where it crosses to the target's tab: high in the gap above it. */
      readonly crossing: number;
    };

/** How far an input's line drops below the input before it turns. */
const FEED_DROP = 12;

/**
 * Between pipeline steps: out of the source's side to its channel right of the column, down
 * to the gap above the target, across to the channel left of the column, down to the port.
 * From an input: down below it (from a step inside an open input, out into the feed channel
 * of that step's own column and down it, past the whole input), across to the feed channel,
 * down to the gap above the target, across over its tab, and down into it.
 */
function columnRoute(from: Point, to: Point, channel: ColumnChannel): Point[] {
  if (channel.kind === "feed") {
    const leaving = channel.beside === null ? [{ x: from.x, y: channel.below }] : [{ x: channel.beside, y: from.y }, { x: channel.beside, y: channel.below }];
    return [from, ...leaving, { x: channel.channel, y: channel.below }, { x: channel.channel, y: channel.crossing }, { x: to.x, y: channel.crossing }, to];
  }
  // From inside an open sub-workflow, past its own inputs: down its column's feed channel, out under the group.
  const right = channel.beside ?? channel.right;
  return [from, { x: right, y: from.y }, { x: right, y: channel.crossing }, { x: channel.left, y: channel.crossing }, { x: channel.left, y: to.y }, to];
}

/**
 * Each pipeline edge's channels in a column: an edge to the next step runs nearest the
 * column and crosses the middle of the gap above its target; an edge that skips steps runs
 * one channel further out per lane (`skipChannels`), and crosses lower in the gap, so no two
 * edges share a run. An input's edge into a step runs in the feed channel, right of those
 * channels and left of the inputs, and crosses high in the gap above its target.
 */
function columnChannels(graph: WorkflowGraph, boxes: ReadonlyMap<string, NodeBox>, origin: ReadonlyMap<string, Point>, plan: SkipPlan): ReadonlyMap<string, ColumnChannel> {
  const parentOf = new Map(graph.nodes.map((node) => [node.id, node.parentId]));
  const band = new Map(graph.nodes.map((node) => [node.id, node.band]));
  const sides = new Map<string | null, { left: number; right: number }>();
  /** By pipeline card: the bottom of the card above it at its level. */
  const above = new Map<string, number>();
  const lastBottom = new Map<string | null, number>();
  for (const node of graph.nodes) {
    const box = boxes.get(node.id);
    const at = origin.get(node.id);
    if (node.band !== "main" || box === undefined || at === undefined) continue;
    const side = sides.get(node.parentId);
    sides.set(node.parentId, { left: Math.min(side?.left ?? Infinity, at.x), right: Math.max(side?.right ?? -Infinity, at.x + box.width) });
    const bottom = lastBottom.get(node.parentId);
    if (bottom !== undefined) above.set(node.id, bottom);
    lastBottom.set(node.parentId, at.y + box.height);
  }
  const { runMargin, runSpacing } = SKIP_CHANNELS;
  const channels = new Map<string, ColumnChannel>();
  /** A level's feed channel: right of its column's channels, left of its inputs; clear of every card at that level. */
  const feedChannelOf = (level: string | null): number | null => {
    const side = sides.get(level);
    return side === undefined ? null : side.right + channelsDepth(plan.lanes.get(level) ?? 0) + COLUMN_LAYOUT.sideGap / 2;
  };
  for (const edge of graph.edges) {
    const level = parentOf.get(edge.target) ?? null;
    const target = nodeAtLevel(parentOf, edge.target, level);
    const side = sides.get(level);
    const gapTop = above.get(target);
    const targetTop = origin.get(target)?.y;
    if (edge.route === "feed") {
      // Into a pipeline step's tab; a feed between inputs keeps its curve within its cluster.
      const input = nodeAtLevel(parentOf, edge.source, level);
      const inputAt = origin.get(input);
      const inputBox = boxes.get(input);
      if (side === undefined || targetTop === undefined || band.get(target) !== "main" || inputAt === undefined || inputBox === undefined) continue;
      const ownChannel = feedChannelOf(level);
      if (ownChannel === null) continue;
      channels.set(edge.id, {
        kind: "feed",
        below: inputAt.y + inputBox.height + FEED_DROP,
        beside: input === edge.source ? null : feedChannelOf(parentOf.get(edge.source) ?? null),
        channel: ownChannel,
        // High in the gap, above where pipeline edges cross it; over the first step, just above its tab.
        crossing: gapTop === undefined ? targetTop - 2 * COLUMN_LAYOUT.feedRise / 3 : gapTop + FEED_DROP,
      });
      continue;
    }
    if (side === undefined || gapTop === undefined || targetTop === undefined) continue;
    const lane = edge.route === "skip" ? 1 + (plan.lane.get(edge.id) ?? 0) : 0;
    const entering = edge.route === "skip" ? 1 + (plan.entering.get(edge.id) ?? 0) : 0;
    const leavesGroup = nodeAtLevel(parentOf, edge.source, level) !== edge.source;
    channels.set(edge.id, {
      kind: "pipeline",
      right: side.right + runMargin + lane * runSpacing,
      left: side.left - runMargin - entering * runSpacing,
      beside: leavesGroup ? feedChannelOf(parentOf.get(edge.source) ?? null) : null,
      crossing: (gapTop + targetTop) / 2 + entering * runSpacing,
    });
  }
  return channels;
}

/** Each node's top left in graph coordinates: boxes are relative to their frame. */
export function absoluteOrigins(graph: WorkflowGraph, boxes: ReadonlyMap<string, NodeBox>): ReadonlyMap<string, Point> {
  const origins = new Map<string, Point>();
  // Parents come before their children (`workflowGraph`).
  for (const node of graph.nodes) {
    const box = boxes.get(node.id);
    if (box === undefined) {
      continue;
    }
    const parent = node.parentId === null ? { x: 0, y: 0 } : origins.get(node.parentId);
    if (parent !== undefined) {
      origins.set(node.id, { x: parent.x + box.x, y: parent.y + box.y });
    }
  }
  return origins;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function towards(from: Point, to: Point, length: number): Point {
  const total = distance(from, to);
  return total === 0 ? from : { x: from.x + ((to.x - from.x) * length) / total, y: from.y + ((to.y - from.y) * length) / total };
}

function n(value: number): string {
  return String(Math.round(value * 10) / 10);
}
